import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || '';

const api = axios.create({
  baseURL: BACKEND_URL,
  headers: { 'Content-Type': 'application/json' },
});

// Add auth token to requests
api.interceptors.request.use((config) => {
  const token = localStorage.getItem('visualte_admin_token');
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

// Handle 401 errors
api.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response?.status === 401) {
      localStorage.removeItem('visualte_admin_token');
      if (window.location.pathname.startsWith('/admin/dashboard')) {
        window.location.href = '/admin';
      }
    }
    return Promise.reject(error);
  }
);

// Public APIs
export const getHealth = () => api.get('/api/health');
export const getProjects = () => api.get('/api/powered');
export const getProject = (slug) => api.get(`/api/powered/${slug}`);
export const submitContact = (data) => api.post('/api/contact', data);

// Auth APIs
export const login = (email, password) => api.post('/api/auth/login', { email, password });
export const verifyAuth = () => api.get('/api/auth/verify');

// Admin APIs
export const adminGetProjects = () => api.get('/api/admin/projects');
export const adminGetProject = (id) => api.get(`/api/admin/projects/${id}`);
export const adminCreateProject = (data) => api.post('/api/admin/projects', data);
export const adminUpdateProject = (id, data) => api.put(`/api/admin/projects/${id}`, data);
export const adminDeleteProject = (id) => api.delete(`/api/admin/projects/${id}`);
export const adminToggleVisibility = (id) => api.patch(`/api/admin/projects/${id}/visibility`);
export const adminUploadFile = (file) => {
  const formData = new FormData();
  formData.append('file', file);
  return api.post('/api/admin/upload', formData, {
    headers: { 'Content-Type': 'multipart/form-data' },
  });
};
export const adminGetInquiries = () => api.get('/api/admin/inquiries');
export const adminMarkInquiryRead = (id) => api.patch(`/api/admin/inquiries/${id}/read`);
export const adminDeleteInquiry = (id) => api.delete(`/api/admin/inquiries/${id}`);

// Blog APIs
export const getPosts = () => api.get('/api/journal');
export const getPost = (slug) => api.get(`/api/journal/${slug}`);
export const adminGetPosts = () => api.get('/api/admin/blog');
export const adminGetPost = (id) => api.get(`/api/admin/blog/${id}`);
export const adminCreatePost = (data) => api.post('/api/admin/blog', data);
export const adminUpdatePost = (id, data) => api.put(`/api/admin/blog/${id}`, data);
export const adminDeletePost = (id) => api.delete(`/api/admin/blog/${id}`);
export const adminTogglePublish = (id) => api.patch(`/api/admin/blog/${id}/publish`);
export const adminTogglePin = (id) => api.patch(`/api/admin/blog/${id}/pin`);
export const adminReorderPosts = (orderedIds) => api.put('/api/admin/blog/reorder', { orderedIds });

// Post Generator APIs
export const getPostSizes = () => api.get('/api/admin/post-generator/sizes');
export const generatePostContent = (data) => api.post('/api/admin/post-generator/generate', data);
export const getPostHistory = () => api.get('/api/admin/post-generator/history');
export const getLiveContext = () => api.get('/api/admin/post-generator/context');
export const saveGeneratedPost = (data) => {
  const formData = new FormData();
  formData.append('content', JSON.stringify(data.content));
  formData.append('aspectRatio', data.aspectRatio);
  formData.append('style', data.style);
  return api.post('/api/admin/post-generator/save', formData, { headers: { 'Content-Type': 'multipart/form-data' } });
};

// Site Settings APIs
export const getSiteSettings = () => api.get('/api/site-settings');
export const adminGetSiteSettings = () => api.get('/api/admin/site-settings');
export const adminUpdateSiteSettings = (data) => api.put('/api/admin/site-settings', data);

// Tracking & Analytics APIs
export const trackPageView = (data) => api.post('/api/track', data);
export const getAnalytics = (params) => api.get('/api/admin/analytics', { params });

export default api;
